/* Today's Parcel Summary — dispatch-team print: one landscape page per company,
 * columns USA | UK | Australia + Others | Emirates | DHL/FedEx/UPS (+ Freeship when outsourced parcels exist). */
import { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { toast } from "sonner";
import { Loader2, Printer } from "lucide-react";

const COL = {
  USA: ["#1d4ed8", "#dbeafe"], UK: ["#b91c1c", "#fee2e2"], AUS_OTHER: ["#047857", "#d1fae5"],
  EMIRATES: ["#b45309", "#fef3c7"], COURIER: ["#6d28d9", "#ede9fe"], FREESHIP: ["#0f766e", "#ccfbf1"],
};
const esc = (s) => String(s ?? "").replace(/[&<>"]/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
const xTag = (n) => (n > 0 ? ` <span class="x">+${n} cancelled</span>` : "");

const sectionHtml = (s, c) => `
  <div class="sec">
    ${s.service ? `<div class="sh" style="background:${c[1]};color:${c[0]};border-color:${c[0]}">${esc(s.service)}</div>` : ""}
    ${s.rows.map(r => `<div class="row"><span class="inv">${esc(r.invoice_no)}</span><span class="dash">–</span><b>${r.parcels}</b>${xTag(r.cancelled)}${r.country_short ? ` <span class="cty">· ${esc(r.country_short)}</span>` : ""}</div>`).join("")}
    <div class="sub">Sub-total <b>${s.total}</b>${xTag(s.cancelled)}</div>
  </div>`;

const pageHtml = (co, label) => `
  <section class="page">
    <header>
      <div><div class="t">Today's Parcel Summary</div><div class="d">${esc(label)}</div></div>
      <div class="co">${esc(co.company === "—" ? "Company not set" : co.company)}</div>
      <div class="gt">Grand total <b>${co.total}</b>${xTag(co.cancelled)}<div class="d">${co.invoices} invoice(s)${co.outsourced ? ` · <span class="os">${co.outsourced} outsourced (Freeship)</span>` : ""}</div></div>
    </header>
    <div class="cols" style="grid-template-columns:repeat(${co.columns.length},1fr)">
      ${co.columns.map(col => { const c = COL[col.id]; return `
        <div class="col" style="border-color:${c[0]}">
          <div class="ch" style="background:${c[0]}">${esc(col.title)}</div>
          <div class="body">${col.sections.length ? col.sections.map(s => sectionHtml(s, c)).join("") : '<div class="none">No parcels</div>'}</div>
          <div class="ct" style="background:${c[1]};color:${c[0]};border-color:${c[0]}">Total <b>${col.total}</b>${xTag(col.cancelled)}</div>
        </div>`; }).join("")}
    </div>
  </section>`;

const CSS = `@page{size:A4 landscape;margin:8mm}*{box-sizing:border-box;-webkit-print-color-adjust:exact;print-color-adjust:exact}
body{font-family:'Segoe UI',Arial,sans-serif;margin:0;color:#0f172a}
.page{page-break-after:always;padding:4px}.page:last-child{page-break-after:auto}
header{display:flex;align-items:center;justify-content:space-between;gap:12px;background:#0f172a;color:#fff;padding:10px 16px;border-radius:8px;margin-bottom:10px}
.t{font-size:20px;font-weight:800;letter-spacing:.3px}.d{font-size:11px;opacity:.8}.co{font-size:18px;font-weight:700;background:#facc15;color:#0f172a;padding:4px 14px;border-radius:999px}
.gt{text-align:right;font-size:13px}.gt b{font-size:24px;margin-left:6px}
.cols{display:grid;gap:8px;align-items:stretch}.col{border:2px solid;border-radius:8px;display:flex;flex-direction:column;overflow:hidden}
.ch{color:#fff;font-size:16px;font-weight:800;text-align:center;padding:7px;letter-spacing:.5px;text-transform:uppercase}
.body{flex:1;padding:6px 8px}.sec{margin-bottom:8px}
.sh{font-size:12px;font-weight:800;padding:3px 8px;border-left:4px solid;border-radius:4px;margin-bottom:3px}
.row{font-size:13px;padding:2px 4px;border-bottom:1px dashed #e2e8f0;font-variant-numeric:tabular-nums}
.inv{font-family:Consolas,monospace;font-weight:600}.dash{margin:0 5px;color:#94a3b8}.cty{color:#475569;font-size:11px;font-weight:600}
.none{color:#94a3b8;text-align:center;font-size:12px;padding:18px 0}.sub{text-align:right;font-size:12px;padding:3px 4px;border-top:1.5px solid #334155;margin-top:2px}
.ct{font-size:15px;text-align:center;padding:6px;border-top:2px solid;font-weight:600}.ct b{font-size:18px}
.x{display:inline-block;background:#fce7f3;color:#9d174d;font-size:10px;font-weight:700;border-radius:999px;padding:0 6px;margin-left:3px}
.os{background:#ccfbf1;color:#0f766e;font-weight:700;border-radius:999px;padding:0 6px}
.t,.co,.gt{white-space:nowrap}.ch{overflow-wrap:anywhere}
@media screen{body{background:#f1f5f9;padding:10px}.page{background:#fff;border-radius:10px;box-shadow:0 1px 3px rgba(15,23,42,.12);padding:10px;margin:0 auto 12px;max-width:1100px}
header{padding:8px 14px}.t{font-size:16px}.co{font-size:14px;padding:3px 12px}.gt b{font-size:20px}.ch{font-size:13px;padding:6px}.ct{font-size:13px}.ct b{font-size:15px}}`;

export const ParcelSummaryButton = ({ params }) => {
  const [busy, setBusy] = useState(false);
  const [doc, setDoc] = useState(null);
  const frame = useRef(null);
  const go = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/reports/parcel-summary`, { headers: getAuthHeader(), params });
      if (!r.data.companies.length) return toast.info("No parcels for this date");
      setDoc({ label: r.data.label, html: `<html><head><title>Parcel Summary ${esc(r.data.label)}</title><style>${CSS}</style></head><body>${r.data.companies.map(c => pageHtml(c, r.data.label)).join("")}</body></html>` });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not build parcel summary");
    } finally { setBusy(false); }
  };
  return (
    <>
      <button type="button" onClick={go} disabled={busy} data-testid="parcel-summary-btn"
        className="print:hidden inline-flex items-center gap-1.5 rounded-lg bg-slate-900 hover:bg-slate-700 text-white px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-60">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Printer className="w-3.5 h-3.5" />} Today's Parcel Summary
      </button>
      <Dialog open={!!doc} onOpenChange={(o) => { if (!o) setDoc(null); }}>
        <DialogContent className="max-w-6xl w-[95vw] p-4" data-testid="parcel-summary-dialog">
          <DialogHeader className="flex-row items-center justify-between space-y-0 pr-8">
            <DialogTitle className="text-sm">Today's Parcel Summary — {doc?.label}</DialogTitle>
            <button type="button" onClick={() => frame.current?.contentWindow?.print()} data-testid="parcel-summary-print"
              className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 hover:bg-slate-700 text-white px-3 py-1.5 text-xs font-semibold transition-colors">
              <Printer className="w-3.5 h-3.5" /> Print / PDF
            </button>
          </DialogHeader>
          {doc && <iframe ref={frame} title="Parcel summary" srcDoc={doc.html} className="w-full h-[75vh] rounded-md border border-slate-200 bg-slate-100" data-testid="parcel-summary-frame" />}
        </DialogContent>
      </Dialog>
    </>
  );
};
